import React, { useState } from 'react';
import {
  FaBookOpen,
  FaBan,
  FaUsers,
  FaComment,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowRight,
  FaCheck,
} from 'react-icons/fa';
import * as S from './GameModes.styles';
import RuleHighlight from './RuleHighlight';
import StepList from './StepList';
import HowToWinCard from './HowToWinCard';

// Icons are mapped by mode.id (never from i18n). Unknown ids fall back to generic.
const MODE_ICONS = {
  'desafio-biblico': FaBookOpen,
  'pregunta-prohibida': FaBan,
  'batalla-biblica': FaUsers,
};

const OUTCOME_ICONS = {
  win: FaCheckCircle,
  continue: FaArrowRight,
  lose: FaTimesCircle,
};

const getModeIcon = (id) => {
  const Icon = MODE_ICONS[id] || FaComment;
  return <Icon />;
};

const getOutcomeIcon = (status) => {
  const Icon = OUTCOME_ICONS[status] || FaArrowRight;
  return <Icon />;
};

/**
 * Interactive "additional modes" section for how-to-play pages.
 * A pill selector switches between modes; the active mode is shown in a
 * gradient-bordered card that re-mounts (fadeInUp) on change.
 *
 * Props:
 *  - items: array of mode objects from i18n. Each mode may have:
 *      id, title, tagline, rule ({title, text}), example ({quote}),
 *      outcomes ([{status, text}]), forbidden/alternatives ({title, items}),
 *      preparation/howToPlay ({title, steps}), howToWin ({title, success, fail, closing}),
 *      closing
 *  - label?: string used as the aria-label of the mode selector
 */
const GameModes = ({ items = [], label }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!Array.isArray(items) || items.length === 0) return null;

  const safeIndex = Math.min(activeIndex, items.length - 1);
  const active = items[safeIndex];

  return (
    <S.Container>
      <S.ModeSelector role="group" aria-label={label}>
        {items.map((mode, idx) => (
          <S.ModeTab
            key={mode.id || idx}
            type="button"
            aria-pressed={idx === safeIndex}
            $active={idx === safeIndex}
            onClick={() => setActiveIndex(idx)}
          >
            <S.ModeTabIcon>{getModeIcon(mode.id)}</S.ModeTabIcon>
            {mode.title}
          </S.ModeTab>
        ))}
      </S.ModeSelector>

      <S.ModeCard key={active.id || safeIndex}>
        <S.ModeHeader>
          <S.ModeIcon>{getModeIcon(active.id)}</S.ModeIcon>
          <div>
            <S.ModeTitle>{active.title}</S.ModeTitle>
            {active.tagline && <S.ModeTagline>{active.tagline}</S.ModeTagline>}
          </div>
        </S.ModeHeader>

        {active.rule && (
          <RuleHighlight title={active.rule.title} text={active.rule.text} />
        )}

        {active.example && active.example.quote && (
          <S.Example>{active.example.quote}</S.Example>
        )}

        {Array.isArray(active.outcomes) && active.outcomes.length > 0 && (
          <S.Outcomes>
            {active.outcomes.map((outcome, idx) => (
              <S.OutcomeChip key={idx} $status={outcome.status}>
                {getOutcomeIcon(outcome.status)}
                <span>{outcome.text}</span>
              </S.OutcomeChip>
            ))}
          </S.Outcomes>
        )}

        {active.forbidden && (
          <S.ChipsBlock>
            <S.ChipsTitle $forbidden>{active.forbidden.title}</S.ChipsTitle>
            <S.Chips>
              {(active.forbidden.items || []).map((item, idx) => (
                <S.Chip key={idx} $forbidden>
                  <FaBan />
                  {item}
                </S.Chip>
              ))}
            </S.Chips>
          </S.ChipsBlock>
        )}

        {active.alternatives && (
          <S.ChipsBlock>
            <S.ChipsTitle>{active.alternatives.title}</S.ChipsTitle>
            <S.Chips>
              {(active.alternatives.items || []).map((item, idx) => (
                <S.Chip key={idx}>
                  <FaCheck />
                  {item}
                </S.Chip>
              ))}
            </S.Chips>
          </S.ChipsBlock>
        )}

        {active.preparation && (
          <S.Nested>
            <StepList
              size="sm"
              title={active.preparation.title}
              steps={active.preparation.steps}
            />
          </S.Nested>
        )}

        {active.howToPlay && (
          <S.Nested>
            <StepList
              size="sm"
              title={active.howToPlay.title}
              steps={active.howToPlay.steps}
            />
          </S.Nested>
        )}

        {active.howToWin && (
          <S.Nested>
            <HowToWinCard
              tone="light"
              title={active.howToWin.title}
              success={active.howToWin.success}
              fail={active.howToWin.fail}
              closing={active.howToWin.closing}
            />
          </S.Nested>
        )}

        {active.closing && <S.Closing>{active.closing}</S.Closing>}
      </S.ModeCard>
    </S.Container>
  );
};

export default GameModes;
